import { useCustom } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { STATUS_LABEL, STATUS_VARIANT, formatBalances } from '@/lib/format';
import type { PsnAccount, QueueGame } from '@/lib/types';

interface AccountsResponse {
  data: PsnAccount[];
  count: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Jogo escolhido na fila — vira ?jogo= na navegação pro Modo Compra. */
  game: QueueGame | null;
}

/**
 * Q-246: escolher em qual conta comprar o jogo. Lista contas funded (primeiro,
 * já têm saldo) e available; clique navega pro Modo Compra da conta.
 */
export function SelectAccountDialog({ open, onOpenChange, game }: Props) {
  const navigate = useNavigate();

  const { data: fundedRes, isLoading: loadingFunded } = useCustom<AccountsResponse>({
    url: '/psn-accounts',
    method: 'get',
    config: { query: { status: 'funded', limit: 100 } },
    queryOptions: { enabled: open },
  });
  const { data: availableRes, isLoading: loadingAvailable } = useCustom<AccountsResponse>({
    url: '/psn-accounts',
    method: 'get',
    config: { query: { status: 'available', limit: 100 } },
    queryOptions: { enabled: open },
  });

  const isLoading = loadingFunded || loadingAvailable;
  const accounts: PsnAccount[] = [
    ...(fundedRes?.data.data ?? []),
    ...(availableRes?.data.data ?? []),
  ];

  const pick = (account: PsnAccount) => {
    onOpenChange(false);
    // titulo semeia a busca do catálogo no Modo Compra (o picker só busca com
    // q>=2 chars — só o product_ref nunca acharia nada); acessos pré-marca o radio.
    const parts = ['modo=compra'];
    if (game?.product_ref) parts.push(`jogo=${encodeURIComponent(game.product_ref)}`);
    if (game?.game_title) parts.push(`titulo=${encodeURIComponent(game.game_title)}`);
    if (game?.access_count === 3 || game?.access_count === 5) {
      parts.push(`acessos=${game.access_count}`);
    }
    navigate(`/contas/${account.id}?${parts.join('&')}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Escolher conta pra compra</DialogTitle>
          <DialogDescription>
            {game ? (
              <>
                Comprar <span className="font-medium text-foreground">{game.game_title}</span> —
                selecione a conta doadora. Contas com saldo aparecem primeiro.
              </>
            ) : (
              'Selecione a conta doadora. Contas com saldo aparecem primeiro.'
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma conta disponível ou com saldo.{' '}
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => {
                onOpenChange(false);
                navigate('/contas/new');
              }}
            >
              Criar conta nova
            </Button>
          </div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-20">Região</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-36">Saldo</TableHead>
                  <TableHead className="w-32">Recovery livres</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((row) => (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => pick(row)}>
                    <TableCell className="font-mono text-sm">{row.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.region}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatBalances(row.balance)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.recovery_codes_available ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
